import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import { Box, Button, SxProps, Theme } from '@mui/material';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import AndGroupButton from './AndGroupButton';
import AudienceList from './AudienceList';
import OrGroupItem from './OrGroupItem';

enum AudienceOperationMode {
  Include = 'include',
  Exclude = 'exclude',
}

const defaultMaxLimitedAndGroups = 3;

interface IProps<V> {
  options: IAudienceControlOption<V>[];
  value: IBaseAudienceTargeting<IAudienceControlOption<V>>;
  onChange: (newValue: IBaseAudienceTargeting<IAudienceControlOption<V>>) => void;
  allowExtendedGrouping: boolean;
  loading?: boolean;
  disabled?: boolean;
  containerSx?: SxProps<Theme>;
  listSx?: SxProps<Theme>;
  maxLimitedAndGroups?: number;
  isModeDisabled?: boolean;
}

const AudienceListContainer = <V extends number | string>({
  options,
  onChange,
  value,
  allowExtendedGrouping,
  loading,
  disabled,
  containerSx,
  listSx,
  maxLimitedAndGroups,
  isModeDisabled,
}: IProps<V>) => {
  const [operationMode, setOperationMode] = useState<AudienceOperationMode>(AudienceOperationMode.Include);

  const isAndHidden = useMemo(() => {
    if (!allowExtendedGrouping) {
      return false;
    }
    return (value?.include ?? []).length > (maxLimitedAndGroups ?? defaultMaxLimitedAndGroups);
  }, [value?.include?.length, allowExtendedGrouping, maxLimitedAndGroups]);

  /**Logic prevention */
  const isAndDisabled = useMemo(
    () => !(value?.include ?? []).length || !(value?.include ?? [])[(value?.include ?? []).length - 1]?.length,
    [value?.include],
  );

  /**Control state */
  const isAndControlDisabled = useMemo(
    () => operationMode === AudienceOperationMode.Exclude || isAndDisabled,
    [operationMode, isAndDisabled],
  );

  const onOperationModeChange = useCallback(
    () =>
      setOperationMode(prevMode =>
        prevMode === AudienceOperationMode.Include ? AudienceOperationMode.Exclude : AudienceOperationMode.Include,
      ),
    [],
  );

  const onIncludeSelectionChange = useCallback(
    (optionValue: IAudienceControlOption<V>, checked: boolean) => {
      if (checked) {
        const targetGroup = value?.include?.at(-1) ?? [];
        const updatedGroup: IAudienceControlOption<V>[] = [...targetGroup, optionValue];
        const updatedInclude: IAudienceControlOption<V>[][] = [...(value?.include ?? []).slice(0, -1), updatedGroup];
        onChange({ ...value, include: updatedInclude });
        return;
      }
      const updatedInclude: IAudienceControlOption<V>[][] = (value?.include ?? []).map(orGroup =>
        orGroup.filter(audience => audience.value !== optionValue.value),
      );
      onChange({ ...value, include: updatedInclude });
    },
    [onChange, value],
  );

  const onExcludeSelectionChange = useCallback(
    (optionValue: IAudienceControlOption<V>, checked: boolean) => {
      if (checked) {
        onChange({ ...value, exclude: [optionValue] });
        return;
      }
      onChange({ ...value, exclude: [] });
    },
    [onChange, value],
  );

  const onListUncheck = useCallback(
    (optionValue: IAudienceControlOption<V>) => {
      const updatedInclude = (value?.include ?? []).map(orGroup =>
        orGroup.filter(audience => audience.value !== optionValue.value),
      );
      const updatedExclude = (value?.exclude ?? []).filter(audience => audience.value !== optionValue.value);
      onChange({ include: updatedInclude, exclude: updatedExclude });
    },
    [onChange, value],
  );

  const handleChange = useCallback(
    (optionValue: IAudienceControlOption<V>, checked: boolean) => {
      if (!checked) {
        return onListUncheck(optionValue);
      }
      if (operationMode === AudienceOperationMode.Include) {
        return onIncludeSelectionChange(optionValue, checked);
      }
      return onExcludeSelectionChange(optionValue, checked);
    },
    [onListUncheck, onIncludeSelectionChange, onExcludeSelectionChange, operationMode],
  );

  const onAddAndClick = useCallback(() => {
    if (isAndDisabled || isAndHidden) {
      return;
    }
    onChange({ ...value, include: [...(value?.include ?? []), []] });
  }, [onChange, isAndDisabled, isAndHidden, value]);

  const onRemoveAndClick = useCallback(
    (groupIndex: number) => {
      onChange({ ...value, include: (value?.include ?? []).filter((_group, index) => index !== groupIndex) });
    },
    [onChange, value],
  );

  const onRemoveIncludeAudience = useCallback(
    (groupIndex: number, audienceValue: IAudienceControlOption<V>['value']) => {
      onChange({
        ...value,
        include: (value?.include ?? []).map((group, index) => {
          if (index !== groupIndex) {
            return group;
          }
          return group.filter(audience => audience.value !== audienceValue);
        }),
      });
    },
    [onChange, value],
  );

  const onRemoveExcludeAudience = useCallback(() => {
    onChange({ ...value, exclude: [] });
  }, [onChange, value]);

  const onRemoveAudience = useCallback(
    (groupIndex: number, operationMode: AudienceOperationMode) =>
      (audienceValue: IAudienceControlOption<V>['value']) => {
        if (operationMode === AudienceOperationMode.Include) {
          return onRemoveIncludeAudience(groupIndex, audienceValue);
        }
        return onRemoveExcludeAudience();
      },
    [onRemoveIncludeAudience, onRemoveExcludeAudience],
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px', ...containerSx }}>
      <div className={classNames('flex justify-between w-full gap-2 print:hidden', { hidden: isAndHidden })}>
        <Button
          variant='contained'
          sx={{ borderRadius: '32px' }}
          disabled={isAndControlDisabled}
          onClick={onAddAndClick}
        >
          AND
        </Button>
        <div className='flex items-center'>
          <span>Mode:</span>
          <Button
            disabled={isModeDisabled}
            variant='contained'
            sx={{ marginLeft: '8px', borderRadius: '32px', textTransform: 'capitalize' }}
            onClick={onOperationModeChange}
            color={operationMode === AudienceOperationMode.Include ? 'primary' : 'error'}
          >
            {operationMode}
          </Button>
        </div>
      </div>
      <div className='w-full flex gap-1 flex-wrap min-h-8'>
        {(value?.include ?? []).map((group, groupIndex) => (
          <div className='flex flex-wrap gap-1 items-center justify-center' key={groupIndex}>
            {groupIndex > 0 && <AndGroupButton groupIndex={groupIndex} onRemove={onRemoveAndClick} />}
            {group.map((audience, index) => (
              <OrGroupItem
                key={audience.value}
                audience={audience}
                first={index === 0}
                last={index === group.length - 1}
                includeBoundaries={group.length > 1 || !!groupIndex}
                onRemove={onRemoveAudience(groupIndex, AudienceOperationMode.Include)}
              />
            ))}
          </div>
        ))}
        {(value?.exclude ?? []).map((audience, index) => (
          <div className='flex flex-wrap gap-1 items-center justify-center' key={index}>
            {<AndGroupButton groupIndex={0} onRemove={onRemoveExcludeAudience} label='EXCLUDE' />}
            <OrGroupItem
              key={audience.value}
              audience={audience}
              first
              last
              includeBoundaries
              onRemove={onRemoveAudience(0, AudienceOperationMode.Exclude)}
            />
          </div>
        ))}
      </div>
      <AudienceList
        options={options}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        loading={loading}
        sx={listSx}
      />
    </Box>
  );
};

export default AudienceListContainer;
